/* ====================================================================
 *  cart — стор кошика. Тут лише стан і оркестрація; уся робота з
 *  даними — у сусідніх модулях: mapping (форма позиції), persist (LS),
 *  remote (Firestore), hydrateImages, migrations.
 *
 *  Гість — localStorage, залогінений — users/{uid}/cartItems,
 *  merge при логіні. Ключ позиції — mapping.makeKey.
 *
 *  ЄДИНИЙ ВЛАСНИК ЗАПИСУ. Кожна мутація сама пише у Firestore; сторінки
 *  і композабли НЕ звертаються до колекції cartItems напряму і НЕ
 *  присвоюють items ззовні — інакше зміна не збережеться.
 * ==================================================================== */

import * as Sentry from "@sentry/nuxt";
import { defineStore } from "pinia";
import { ref, computed, watch, type Ref } from "vue";
import { useNuxtApp, onNuxtReady } from "#app";
import { useAuthStore } from "~/stores/auth";
import {
  makeKey,
  canonicalKeyOf,
  toCartItem,
  mergeCartLists,
  type CartItem,
} from "~/stores/cart/mapping";
import { loadFromLS, saveToLS, clearLS } from "~/stores/cart/persist";
import {
  fetchCartItems,
  subscribeCartItems,
  upsertItems,
  deleteKey,
  deleteKeys,
  clearRemoteCart,
} from "~/stores/cart/remote";
import { hydrateMissingImages } from "~/stores/cart/hydrateImages";
import { migrateCartKeys } from "~/stores/cart/migrations";

export type { CartItem } from "~/stores/cart/mapping";

/**
 * Вхід add() навмисно нестрогий: викликають і .js-композабли, і місця,
 * де поля товару приходять як unknown із Firestore. Нормалізація одна —
 * у mapping.toCartItem.
 */
export interface AddCartItemInput {
  productId?: unknown;
  variantId?: unknown;
  sku?: unknown;
  slug?: unknown;
  title?: unknown;
  price?: unknown;
  image?: unknown;
  color?: unknown;
  size?: unknown;
  isExclusive?: unknown;
  exclusiveType?: unknown;
  badgeText?: unknown;
  qty?: unknown;
}

/** Помилка синку кошика ніколи не ламає UI, але й ніколи не гине мовчки. */
function reportSyncError(where: string, e?: unknown) {
  console.warn(`[cart-sync] ${where}`, e);
  try {
    Sentry.withScope((scope) => {
      scope.setTag("area", "cart-sync");
      scope.setContext("cartSync", { where });
      Sentry.captureException(
        e instanceof Error ? e : new Error(`cart-sync: ${where}`),
      );
    });
  } catch {
    /* Sentry не має ламати кошик */
  }
}

export const useCartStore = defineStore("cart", () => {
  // stores/auth.js — ще JS (п.13): без касту поля інферяться як never
  const auth = useAuthStore() as unknown as {
    isAuthed?: boolean;
    user?: { uid?: string } | null;
  };

  // 🔵 Take $db lazily (avoids capturing undefined during early hydration)
  const getDb = () => {
    const { $db } = useNuxtApp();
    return $db;
  };
  const uidOf = () => String(auth.user?.uid || "");

  const items: Ref<CartItem[]> = ref([]);

  function setItemsFromRemote(list: CartItem[]) {
    items.value = Array.isArray(list) ? list : [];
  }

  const count = computed(() =>
    items.value.reduce((s, i) => s + Number(i.qty || 0), 0),
  );
  const subtotal = computed(() =>
    items.value.reduce(
      (s, i) => s + Number(i.price || 0) * Number(i.qty || 0),
      0,
    ),
  );

  function hydrateImages(list: CartItem[]) {
    return hydrateMissingImages(getDb(), uidOf(), list);
  }

  /**
   * Персист гостьового кошика. Кличеться КОЖНОЮ мутацією явно —
   * deep-вотчер нижче лишається тільки страхувальною сіткою. Раніше він
   * був єдиним шляхом, тобто персист залежав від того, чи встиг
   * onNuxtReady (він іде через requestIdleCallback) зареєструвати вотчер.
   */
  function persistGuest() {
    if (auth.isAuthed) return;
    saveToLS(items.value);
  }

  /** Записати позицію у віддалений кошик (для залогінених). */
  function persistItem(item: CartItem | undefined) {
    if (!item) return;
    const db = getDb();
    if (!db || !auth.isAuthed) return;
    // fire-and-forget: локальний стан уже оновлено, а Firestore одразу
    // віддає власний запис у снапшот із кешу
    upsertItems(db, uidOf(), [item]).catch((e) =>
      reportSyncError("persistItem", e),
    );
  }

  // ------- CRUD -------
  function add(input: AddCartItemInput) {
    const productId = String(input?.productId || "");
    if (!productId) return;

    const key = makeKey({
      productId,
      variantId: String(input.variantId || ""),
      color: String(input.color || ""),
      size: String(input.size || ""),
    });
    const idx = items.value.findIndex((i) => i.key === key);
    const addQty = Number(input.qty || 1);

    if (idx === -1) {
      items.value.unshift(toCartItem(key, { ...input, productId }));
    } else {
      const cur = items.value[idx];
      cur.qty = Number(cur.qty) + addQty;

      // backfill (на випадок старих записів)
      if (!cur.variantId && input.variantId)
        cur.variantId = String(input.variantId);
      if (!cur.sku && input.sku) cur.sku = String(input.sku);
      if (!cur.isExclusive && input.isExclusive) cur.isExclusive = true;
      if (!cur.exclusiveType && input.exclusiveType)
        cur.exclusiveType = String(input.exclusiveType);
      if (!cur.badgeText && input.badgeText)
        cur.badgeText = String(input.badgeText);
    }

    // If image wasn't provided (or exclusive should use variant image), hydrate it from the variant doc.
    // Fire-and-forget to keep add() synchronous; гідруємо ЛИШЕ зачеплену
    // позицію — решта кошика вже пройшла гідрацію при своєму додаванні.
    const touched = items.value.find((i) => i.key === key);
    persistItem(touched);
    persistGuest();
    if (touched) {
      setTimeout(() => {
        hydrateImages([touched]);
      }, 0);
    }
  }

  /**
   * Кількість позиції. qty <= 0 — це видалення (так поводилась кнопка
   * «−» на сторінці кошика). Верхня межа 99 була в сторі й раніше.
   */
  async function setQty(key: string, qty: number) {
    const n = Number(qty || 0);
    if (n <= 0) return remove(key);

    const i = items.value.find((it) => it.key === key);
    if (!i) return;
    i.qty = Math.max(1, Math.min(99, n));
    persistItem(i);
    persistGuest();
  }

  async function remove(key: string) {
    items.value = items.value.filter((i) => i.key !== key);
    persistGuest();

    // видаляємо з віддаленого кошика лише за явної дії
    const db = getDb();
    if (db && auth.isAuthed) await deleteKey(db, uidOf(), key);
  }

  /** Видалити кілька позицій однією пачкою (прун недоступних товарів). */
  async function removeMany(keys: string[]) {
    const list = (keys || []).filter(Boolean);
    if (!list.length) return;
    const drop = new Set(list);
    items.value = items.value.filter((i) => !drop.has(i.key));
    persistGuest();

    const db = getDb();
    if (db && auth.isAuthed) await deleteKeys(db, uidOf(), list);
  }

  async function clear() {
    // спочатку очистимо remote, потім локальний
    const db = getDb();
    if (db && auth.isAuthed) await clearRemoteCart(db, uidOf());
    items.value = [];
    clearLS();
  }

  // ------- Firestore sync (для авторизованих) -------
  // простий upsert усіх позицій (викликаємо з debounce)
  async function saveToRemote() {
    const db = getDb();
    if (!db || !auth.isAuthed) return;
    {
      // Normalize keys: if variantId is present but key is still old format, upgrade it.
      // IMPORTANT: if the key changes, delete the old remote doc, otherwise it will "come back" from Firestore.
      const staleKeys: string[] = [];
      for (const i of items.value) {
        const shouldKey = canonicalKeyOf(i);
        if (shouldKey !== i.key) {
          staleKeys.push(i.key);
          i.key = shouldKey;
        }
      }

      if (staleKeys.length) {
        // Delete only old keys that are not used as canonical keys anymore
        const canonical = new Set(items.value.map((x) => x.key));
        await deleteKeys(
          db,
          uidOf(),
          staleKeys.filter((k) => k && !canonical.has(k)),
        );
      }

      await upsertItems(db, uidOf(), items.value);
    }
  }

  /**
   * Злиття гостьового кошика з віддаленим при логіні.
   *
   * Гостьовий кошик береться ЗІ СТАНУ СТОРА, а localStorage — лише
   * запасне джерело. Причина: LS — це дзеркало стану, і якщо запис у
   * нього не відбувся (гонка з onNuxtReady, приватний режим, збій
   * серіалізації), мердж мовчки зливав ПОРОЖНЬО, після чого clearLS
   * добивав гостьовий кошик остаточно.
   */
  async function mergeGuestIntoRemote(
    guest: CartItem[],
    remoteRows: CartItem[],
  ): Promise<{ merged: number }> {
    const merged = mergeCartLists(remoteRows, guest);
    setItemsFromRemote(merged);
    await saveToRemote();
    return { merged: merged.length };
  }

  // ------- Живий кошик (підписка на Firestore) -------
  // Життєвий цикл стартує сторінка кошика: підписка потрібна лише там,
  // де кошик редагують, і не має висіти на всю сесію.
  // uid, для якого гостьовий кошик уже влито: повторний виклик
  // onAuthChanged не має вливати ті самі позиції вдруге
  let lastSyncedUid = "";

  let unsubSnapshot: (() => void) | null = null;
  let stopUidWatch: (() => void) | null = null;
  let subscribedUid = "";

  function dropSnapshot() {
    if (unsubSnapshot) {
      unsubSnapshot();
      unsubSnapshot = null;
    }
    subscribedUid = "";
  }

  function subscribeRemote() {
    if (stopUidWatch) return; // вже активна
    stopUidWatch = watch(
      () => uidOf(),
      (uid) => {
        // перепідписуємось лише коли uid реально змінився
        if (uid === subscribedUid) return;
        dropSnapshot();
        if (!uid) return;
        const db = getDb();
        if (!db) return;
        subscribedUid = uid;
        unsubSnapshot = subscribeCartItems(db, uid, setItemsFromRemote, (e) =>
          reportSyncError("subscribeCartItems", e),
        );
      },
      { immediate: true },
    );
  }

  function unsubscribeRemote() {
    if (stopUidWatch) {
      stopUidWatch();
      stopUidWatch = null;
    }
    dropSnapshot();
  }

  // ------- Ініціалізація -------
  if (import.meta.client) {
    onNuxtReady(() => {
      // при старті відновлюємо лише гостьовий кошик
      if (!auth.isAuthed) {
        items.value = loadFromLS();
      }

      // Страхувальна сітка: основний персист гостя робить persistGuest()
      // у кожній мутації. Для залогінених джерело правди — Firestore.
      watch(
        items,
        () => {
          if (!auth.isAuthed) saveToLS(items.value);
        },
        { deep: true },
      );
    });
  }

  // викликається з плагіна при зміні auth
  async function onAuthChanged() {
    if (!auth.isAuthed) {
      // логаут: починаємо гостьовий кошик "з нуля"
      lastSyncedUid = "";
      setItemsFromRemote([]);
      clearLS();
      return;
    }

    const uid = uidOf();

    // Гостьовий кошик фіксуємо ДО будь-яких записів в items: нижче
    // міграція може підставити туди віддалені позиції, і тоді ми злили б
    // їх самі з собою, подвоївши кількості. З тієї ж причини повторний
    // синк того самого uid вливає порожній гість — інакше вже влиті
    // позиції додались би до себе.
    const guest =
      lastSyncedUid === uid
        ? []
        : items.value.length
          ? items.value.slice()
          : loadFromLS();

    const db = getDb();
    if (!db) {
      // LS НЕ чистимо: гостьовий кошик має пережити збій синку
      reportSyncError("onAuthChanged: no db");
      return;
    }

    try {
      // одне читання колекції на логін: його результат використовують
      // і міграція ключів, і merge гостьового кошика
      let remoteRows = await fetchCartItems(db, uidOf());
      const migrated = await migrateCartKeys(db, uidOf(), remoteRows);
      if (migrated) {
        remoteRows = migrated;
        setItemsFromRemote(migrated);
      }

      const { merged } = await mergeGuestIntoRemote(guest, remoteRows);
      lastSyncedUid = uid;

      // Чистимо LS лише коли гостьові позиції справді доїхали в кошик.
      if (!guest.length || merged >= guest.length) {
        clearLS();
      } else {
        reportSyncError(
          `onAuthChanged: merge lost items (guest=${guest.length}, merged=${merged})`,
        );
      }

      await hydrateImages(items.value);
    } catch (e) {
      // LS лишається недоторканим — кошик можна відновити перезаходом
      reportSyncError("onAuthChanged", e);
    }
  }

  return {
    items,
    count,
    subtotal,
    add,
    setQty,
    remove,
    removeMany,
    clear,
    subscribeRemote,
    unsubscribeRemote,
    onAuthChanged,
  };
});
